import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import analytics, { UserTraits, GroupTraits } from "@/lib/segment";
import posthog from "@/lib/posthog";

/**
 * Hook to manage Segment identity lifecycle based on Supabase auth state.
 * 
 * - When a user signs in: Calls analytics.identify() with user traits,
 *   then analytics.group() with company traits for B2B tracking.
 * - When a user signs out: Calls analytics.reset() to clear identity.
 * 
 * This hook should be used once at the app root level (App.tsx).
 */
export function useAnalyticsIdentity(): void {
  const identifiedUserRef = useRef<string | null>(null);

  useEffect(() => {
    // Set up auth state listener - fires immediately with current session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Handle sign out first (sync operation)
        if (event === "SIGNED_OUT") {
          analytics.reset();
          posthog.reset();
          identifiedUserRef.current = null;
          return;
        }

        // Handle sign in - fire and forget to avoid blocking auth flow
        if (session?.user) {
          const user = session.user;

          // Avoid re-identifying the same user
          if (identifiedUserRef.current === user.id) {
            return;
          }

          // Mark as identified immediately to prevent duplicate calls
          identifiedUserRef.current = user.id;

          // Build and send analytics asynchronously (non-blocking)
          (async () => {
            try {
              const traits: UserTraits = {
                email: user.email,
                created_at: user.created_at,
              };

              // Add metadata if available
              if (user.user_metadata) {
                if (user.user_metadata.full_name) {
                  traits.full_name = user.user_metadata.full_name;
                }
                if (user.user_metadata.subscription_tier) {
                  traits.subscription_tier = user.user_metadata.subscription_tier;
                }
              }

              // Try to get company info for both identify traits and group call
              const { data: member } = await supabase
                .from("company_members")
                .select("company_id, role, companies(name, subscription_tier)")
                .eq("user_id", user.id)
                .maybeSingle();

              if (member) {
                traits.company_id = member.company_id;
                traits.role = member.role;
                
                const company = member.companies as { name?: string; subscription_tier?: string } | null;
                if (company) {
                  traits.company_name = company.name;
                  traits.subscription_tier = company.subscription_tier;
                }
              }

              // 1. Identify the user in both Segment and PostHog
              analytics.identify(user.id, traits);
              posthog.identify(user.id, traits);

              // 2. Group the user with their company for B2B tracking
              if (member?.company_id) {
                const company = member.companies as { name?: string; subscription_tier?: string } | null;
                
                // Fetch member count for the company
                const { count: memberCount } = await supabase
                  .from("company_members")
                  .select("*", { count: "exact", head: true })
                  .eq("company_id", member.company_id);

                const groupTraits: GroupTraits = {
                  name: company?.name || "Unknown Company",
                  subscription_tier: company?.subscription_tier || "free",
                  member_count: memberCount ?? 1,
                };

                analytics.group(member.company_id, groupTraits);
              }
            } catch (error) {
              // Analytics failed, but don't block the user experience
              console.debug("[Analytics] Could not complete identify/group:", error);
            }
          })();
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);
}

export default useAnalyticsIdentity;
